import type {
  ActiveRunPhase,
  AgentStepEvent,
  AiRunPhaseEvent,
  MessageGenerationStep,
} from "@/features/ai-agent/types/agent-step";

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

import {
  agentStepKeyOf,
  isApprovalStep,
  isSettledStep,
  sortAgentSteps,
  upsertAgentStep,
} from "@/features/ai-agent/helpers/agent-steps";
import {
  isReplyStep,
  isNoReplyStep,
} from "@/features/ai-agent/helpers/agent-step-predicates";
import { CONVERSATION_STEP_SOURCES } from "@/features/ai-agent/constants/agent-steps";

interface LivePhase {
  generationId: string;
  phase: ActiveRunPhase;
  at: number;
}

/** A phase this old with no end event is a dropped one, not a slow run: the
 *  last resort for a lost `done` on a run that never stepped, which no poll
 *  can heal. */
const PHASE_STALE_MS = 3 * 60 * 1000;

/** Runs the backend said are done, so a late phase or step event for one
 *  cannot bring it back. Events are fire-and-forget and arrive out of order. */
const FINISHED_RUNS_KEPT = 50;

interface LiveRun {
  key: string;
  runningIds: string[];
  generationId?: string | null;
}

const isFreshPhase = (live: LivePhase | undefined): live is LivePhase =>
  Boolean(live && Date.now() - live.at < PHASE_STALE_MS);

interface AgentStepsState {
  /** Live step timelines keyed by correlation id (messageId ?? generationId). */
  stepsByKey: Record<string, MessageGenerationStep[]>;
  /** Pending-approval execute_tool step ids per contact - drives the
   *  "AI is waiting for approval" conversation-list preview. */
  waitingApprovalStepsByContact: Record<string, string[]>;
  /** The run Echo is working on right now, per contact: its timeline key and
   *  the ids of steps still `running`. Drives the composer card's working
   *  state and the conversation-list "drafting" chip. Cleared by the run's
   *  `done` phase, or by a snapshot holding its reply when that was lost. */
  liveRunByContact: Record<string, LiveRun>;
  /** Every timeline key a contact's inbox step events landed under. A run a
   *  teammate started is keyed by its generation, which no message event
   *  carries: the draft finder reads these so the draft shows in the same
   *  update that ends the run. */
  stepKeysByContact: Record<string, string[]>;
  /** What each contact's live run is doing between steps (reading, writing,
   *  sending), from AI_RUN_PHASE. It arrives before the run's first step, so
   *  it is also what shows a run that has not stepped yet. Cleared by `done`
   *  or `waiting`. */
  phaseByContact: Record<string, LivePhase>;
  finishedGenerationIds: string[];
  /** Correlation key of the most recent preview-sourced step - lets the
   *  studio chat panel pick up an in-flight run's timeline. */
  latestPreviewKey: string | null;
  /** Forget the last preview run's key - called when a new preview send starts
   *  so the panel only adopts steps streamed by the run it's waiting on. */
  clearLatestPreviewKey: () => void;
  /** Route an AI_RUN_PHASE pusher event into the store. */
  applyPhaseEvent: (event: AiRunPhaseEvent) => void;
  /** Route an AGENT_STEP pusher event into the store. */
  applyStepEvent: (event: AgentStepEvent) => void;
  /** Overwrite-by-id upsert - used by realtime events and optimistic writes. */
  upsertStep: (step: MessageGenerationStep) => void;
  /** Merge a fetched snapshot in; steps already in the store win (they carry
   *  realtime/optimistic state the snapshot may predate). */
  seedSteps: (key: string, steps: MessageGenerationStep[]) => void;
  /** Authoritative overwrite - used after a 409 refetch. */
  replaceSteps: (key: string, steps: MessageGenerationStep[]) => void;
  /** Drop a step id from every contact's waiting-approval list. */
  resolveWaitingApproval: (stepId: string) => string[];
  /** Undo an optimistic resolve: put the step back on those contacts' lists. */
  restoreWaitingApproval: (stepId: string, contactIds: string[]) => void;
  /** Flag a fetched step onto its contact's waiting-approval list, if the
   *  copy in the store is still waiting: the fetch-on-open recovery for the
   *  permission card, which the realtime event otherwise flags. */
  flagWaitingApproval: (contactId: string, step: MessageGenerationStep) => void;
}

export const useAgentStepsStore = create<AgentStepsState>()(
  immer((set, get) => ({
    stepsByKey: {},
    waitingApprovalStepsByContact: {},
    liveRunByContact: {},
    stepKeysByContact: {},
    phaseByContact: {},
    finishedGenerationIds: [],
    latestPreviewKey: null,
    clearLatestPreviewKey() {
      set((state) => {
        state.latestPreviewKey = null;
      });
    },
    applyPhaseEvent({ contactId, generationId, phase }) {
      if (get().finishedGenerationIds.includes(generationId)) return;
      set((state) => {
        const isThisRun =
          state.phaseByContact[contactId]?.generationId === generationId;

        if (phase === "waiting" || phase === "done") {
          if (isThisRun) delete state.phaseByContact[contactId];
        } else {
          state.phaseByContact[contactId] = {
            generationId,
            phase,
            at: Date.now(),
          };
        }
        if (phase !== "done") return;

        state.finishedGenerationIds = [
          ...state.finishedGenerationIds,
          generationId,
        ].slice(-FINISHED_RUNS_KEPT);
        const run = state.liveRunByContact[contactId];

        // Perception steps predate the generation, so a run keyed off one
        // carries no id yet; it is this contact's run all the same.
        if (run && (!run.generationId || run.generationId === generationId)) {
          delete state.liveRunByContact[contactId];
        }
      });
    },
    applyStepEvent(event) {
      const key = event.correlationId ?? agentStepKeyOf(event.step);

      if (!key) return;

      set((state) => {
        const held = state.stepsByKey[key]?.find((s) => s.id === event.step.id);

        // Each step's events are separate fire-and-forget emits, so a
        // `running` can land after the `resolved` it preceded. A settled step
        // never goes back: the late event would leave it spinning and the card
        // pulsing on an old step instead of the newest one.
        if (held && isSettledStep(held) && !isSettledStep(event.step)) return;

        // A truncated event carries identity and status only - its body was too
        // large for Pusher. Overwriting with the nulls would blank a body we
        // already hold; the handler's refetch fills it when we hold none.
        const previous = event.truncated ? held : undefined;
        const step = previous
          ? {
              ...event.step,
              output: previous.output,
              meta: previous.meta,
              error: previous.error,
            }
          : event.step;

        state.stepsByKey[key] = upsertAgentStep(state.stepsByKey[key], step);

        if (event.source === "preview") {
          state.latestPreviewKey = key;
        }

        // A superseded step is an older run being retired, often by the run
        // that is about to land its draft: it must not take over the live run.
        // Only the backend's `done` phase ends a run; a step of one it already
        // ended is a late event.
        if (
          CONVERSATION_STEP_SOURCES.has(event.source) &&
          event.contactId &&
          event.step.status !== "superseded"
        ) {
          const { status, id, generationId } = event.step;
          const run = state.liveRunByContact[event.contactId];
          const sameRun = run?.key === key ? run.runningIds : [];
          const without = sameRun.filter((stepId) => stepId !== id);
          const keys = state.stepKeysByContact[event.contactId] ?? [];

          if (!keys.includes(key)) {
            state.stepKeysByContact[event.contactId] = [...keys, key];
          }

          // A dropped settle or `done` is healed by seedSteps, fed by the live
          // run's 5s poll (useEchoRun).
          if (
            !generationId ||
            !state.finishedGenerationIds.includes(generationId)
          ) {
            state.liveRunByContact[event.contactId] = {
              key,
              runningIds: status === "running" ? [...without, id] : without,
              generationId: generationId ?? run?.generationId ?? null,
            };
          }
        }

        // Inbox approval flag for the conversation-list preview.
        if (
          CONVERSATION_STEP_SOURCES.has(event.source) &&
          event.contactId &&
          isApprovalStep(event.step)
        ) {
          const waiting =
            state.waitingApprovalStepsByContact[event.contactId] ?? [];
          const without = waiting.filter((id) => id !== event.step.id);

          state.waitingApprovalStepsByContact[event.contactId] =
            event.step.status === "waiting"
              ? [...without, event.step.id]
              : without;
        }
      });
    },
    upsertStep(step) {
      const key = agentStepKeyOf(step);

      if (!key) return;

      set((state) => {
        state.stepsByKey[key] = upsertAgentStep(state.stepsByKey[key], step);
      });
    },
    seedSteps(key, steps) {
      const existing = get().stepsByKey[key] ?? [];
      const seededById = new Map(steps.map((s) => [s.id, s]));

      // Realtime flips that landed after the fetch must still win, so a snapshot
      // may only move a step onwards, never back. Two transitions qualify:
      //
      //  - off `waiting`. Without it a draft the server already superseded keeps
      //    rendering "Awaiting review", and the composer keeps offering it.
      //  - onto a settled status from an unsettled one. This is the recovery for
      //    a dropped AGENT_STEP event: a tool call whose `running` event arrived
      //    but whose `resolved` one did not spins in the timeline forever. The
      //    timeline's poll fetches the truth every 30s; this is what stops the
      //    store from throwing it away on arrival. Restricting it to *settled*
      //    seeded rows is what keeps it safe - a snapshot taken before a
      //    realtime flip can never drag a step back to `waiting` or `running`.
      const refreshed = existing.map((step) => {
        const seeded = seededById.get(step.id);

        if (!seeded) return step;

        const leavesWaiting =
          step.status === "waiting" && seeded.status !== "waiting";
        const settles = !isSettledStep(step) && isSettledStep(seeded);
        // Held from a truncated event: identity and status only. The snapshot
        // is the refetch that event asked for; without its body a parked ask
        // has no `meta.requiresApproval` and its card never comes up.
        const fillsBody =
          step.status === seeded.status && !step.meta && Boolean(seeded.meta);

        return leavesWaiting || settles || fillsBody ? seeded : step;
      });

      const known = new Set(existing.map((s) => s.id));
      const missing = steps.filter((s) => !known.has(s.id));
      const unchanged = refreshed.every((step, i) => step === existing[i]);

      if (missing.length === 0 && unchanged) return;

      const flippedIds = new Set(
        existing
          .filter((step, i) => refreshed[i].status !== step.status)
          .map((s) => s.id),
      );

      set((state) => {
        const merged = sortAgentSteps([...refreshed, ...missing]);

        state.stepsByKey[key] = merged;

        // The live run on this timeline follows the snapshot too: a run whose
        // reply or stop step only the snapshot holds has ended, and a step the
        // snapshot settled is no longer running.
        const newest = merged.at(-1);
        const runEnded =
          newest &&
          newest.status !== "running" &&
          (isReplyStep(newest) || isNoReplyStep(newest));

        for (const [contactId, run] of Object.entries(state.liveRunByContact)) {
          if (run.key !== key) continue;
          if (runEnded) {
            delete state.liveRunByContact[contactId];
            if (
              state.phaseByContact[contactId]?.generationId ===
              newest.generationId
            ) {
              delete state.phaseByContact[contactId];
            }
            continue;
          }
          run.runningIds = run.runningIds.filter(
            (id) => merged.find((step) => step.id === id)?.status === "running",
          );
        }

        // A snapshot that resolves a waiting step must also clear its
        // conversation-list approval flag - that flag is otherwise only
        // dropped by the realtime event this snapshot is recovering from.
        if (flippedIds.size === 0) return;
        for (const contactId of Object.keys(
          state.waitingApprovalStepsByContact,
        )) {
          const waiting = state.waitingApprovalStepsByContact[contactId];
          const next = waiting.filter((id) => !flippedIds.has(id));

          if (next.length !== waiting.length) {
            state.waitingApprovalStepsByContact[contactId] = next;
          }
        }
      });
    },
    replaceSteps(key, steps) {
      set((state) => {
        state.stepsByKey[key] = sortAgentSteps(steps);
      });
    },
    flagWaitingApproval(contactId, step) {
      const key = agentStepKeyOf(step);
      const live = key
        ? get().stepsByKey[key]?.find((s) => s.id === step.id)
        : undefined;

      if (!live || live.status !== "waiting" || !isApprovalStep(live)) return;
      set((state) => {
        const waiting = state.waitingApprovalStepsByContact[contactId] ?? [];

        if (!waiting.includes(step.id)) {
          state.waitingApprovalStepsByContact[contactId] = [
            ...waiting,
            step.id,
          ];
        }
      });
    },
    resolveWaitingApproval(stepId) {
      const flaggedFor = Object.entries(get().waitingApprovalStepsByContact)
        .filter(([, ids]) => ids.includes(stepId))
        .map(([contactId]) => contactId);

      if (!flaggedFor.length) return flaggedFor;
      set((state) => {
        for (const contactId of flaggedFor) {
          state.waitingApprovalStepsByContact[contactId] =
            state.waitingApprovalStepsByContact[contactId].filter(
              (id) => id !== stepId,
            );
        }
      });

      return flaggedFor;
    },
    restoreWaitingApproval(stepId, contactIds) {
      set((state) => {
        for (const contactId of contactIds) {
          const waiting = state.waitingApprovalStepsByContact[contactId] ?? [];

          if (!waiting.includes(stepId)) {
            state.waitingApprovalStepsByContact[contactId] = [
              ...waiting,
              stepId,
            ];
          }
        }
      });
    },
  })),
);

/** Whether Echo is mid-run on a contact - the list chip's only question. The
 *  whole run counts, gaps between steps included: reading only the running
 *  steps flipped the chip back to "Draft ready" between every two. A run
 *  parked on a person is waiting on them, not drafting. */
export const useIsEchoDrafting = (contactId: string): boolean =>
  useAgentStepsStore(
    (state) =>
      (Boolean(state.liveRunByContact[contactId]) ||
        isFreshPhase(state.phaseByContact[contactId])) &&
      !state.waitingApprovalStepsByContact[contactId]?.length,
  );

/** What the contact's live run is doing between steps, or null. */
export const useEchoRunPhase = (
  contactId: string | undefined,
): ActiveRunPhase | null =>
  useAgentStepsStore((state) => {
    const live = contactId ? state.phaseByContact[contactId] : undefined;

    return isFreshPhase(live) ? live.phase : null;
  });

/** Whether a contact has an inbox action waiting for human approval. */
export const useHasWaitingApproval = (contactId: string): boolean =>
  useAgentStepsStore(
    (state) =>
      (state.waitingApprovalStepsByContact[contactId]?.length ?? 0) > 0,
  );

const NO_STEPS: MessageGenerationStep[] = [];

/** The actions waiting on this contact, as steps, so a surface can render
 *  their approval cards outside the timeline. */
export const useWaitingApprovalSteps = (
  contactId: string,
): MessageGenerationStep[] => {
  // Shallow on the resolved steps, not the whole timeline map: step events
  // for every contact in the workspace replace that map, and each one was
  // re-rendering the composer footer.
  return useAgentStepsStore(
    useShallow((state) => {
      const ids = state.waitingApprovalStepsByContact[contactId];

      if (!ids?.length) return NO_STEPS;
      const all = Object.values(state.stepsByKey).flat();

      return (
        ids
          .map((id) => all.find((step) => step.id === id))
          // An answer or approval flips the step optimistically: the card goes
          // with it, and comes back if the request fails and rolls it back.
          .filter(
            (step): step is MessageGenerationStep => step?.status === "waiting",
          )
      );
    }),
  );
};

/** Every ask in the run behind `step`, answered or not, so the permission
 *  card can say how far through them a person is. */
export const useRunApprovalSteps = (
  step: MessageGenerationStep | undefined,
): MessageGenerationStep[] => {
  const key = step ? agentStepKeyOf(step) : null;

  return useAgentStepsStore(
    useShallow((state) =>
      key && step
        ? (state.stepsByKey[key] ?? NO_STEPS).filter(
            (one) =>
              isApprovalStep(one) && one.generationId === step.generationId,
          )
        : NO_STEPS,
    ),
  );
};
